import { mutators } from "@oblique/shared/zero"
import { useZero } from "@rocicorp/zero/react"
import type { Edge, Node, OnEdgesChange, OnNodesChange } from "@xyflow/react"
import {
	Background,
	ConnectionLineType,
	type EdgeTypes,
	ReactFlow,
	useReactFlow,
	useStore,
	useStoreApi,
} from "@xyflow/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router"
import { toast } from "sonner"
import { v7 as uuidv7 } from "uuid"
import {
	ConnectionPreview,
	CursorLayer,
	DragPreview,
	SelectionPreview,
} from "@/components/cursor"
import { CustomControls } from "@/components/node-viewer/custom-controls"
import { useWorkspaceInteractions } from "@/hooks/project/useWorkspaceInteractions"
import type { ProjectNodeData } from "@/types/project"
import { useNodeCopyPaste } from "../../hooks/project/use-node-copy-paste"
import { isEditableTarget } from "../../utils/selection-checks"
import {
	type BuiltInNodeTemplate,
	CommandPalette,
	type PaletteState,
} from "./command-palette/index"
import { BaseNode } from "./nodes/base-node"
import {
	imageNodeCode,
	imageNodeLabel,
	isImageFile,
	MAX_IMAGE_NODE_FILE_SIZE,
	readImageFileAsDataURL,
} from "./nodes/image-node"
import { NodeConnectionLine } from "./nodes/node-connection-line"
import { NodeEdge } from "./nodes/node-edge"

const edgeTypes: EdgeTypes = {
	oblique: NodeEdge,
}

const nodeTypes = {
	python: BaseNode,
	react: BaseNode,
}

export const NodeEditor = ({
	nodes,
	edges,
	onNodesChange,
	onEdgesChange,
}: {
	nodes: Node<ProjectNodeData>[]
	edges: Edge[]
	onNodesChange: OnNodesChange<Node<ProjectNodeData>>
	onEdgesChange: OnEdgesChange<Edge>
}) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const pointerClientRef = useRef<{ x: number; y: number } | null>(null)

	const z = useZero()
	const { projectId } = useParams<{ projectId: string }>()
	const reactFlow = useReactFlow<Node<ProjectNodeData>, Edge>()
	const storeApi = useStoreApi<Node<ProjectNodeData>, Edge>()

	const flowNodes = useStore((store) => store.nodes)
	const selectedNodes = useMemo(
		() => flowNodes.filter((node) => node.selected) as Node<ProjectNodeData>[],
		[flowNodes],
	)
	const [paletteState, setPaletteState] = useState<PaletteState>({
		mode: "closed",
	})
	const { selection, handlers } = useWorkspaceInteractions()
	const { copySelection, hasClipboardSelection, pasteSelection } =
		useNodeCopyPaste()
	const setSelectedNodeIds = useCallback(
		(nodeIds: string[]) => {
			const selectedNodeIds = new Set(nodeIds)
			const state = storeApi.getState()
			state.triggerNodeChanges(
				state.nodes.map((node) => ({
					id: node.id,
					type: "select" as const,
					selected: selectedNodeIds.has(node.id),
				})),
			)
			state.triggerEdgeChanges(
				state.edges.map((edge) => ({
					id: edge.id,
					type: "select" as const,
					selected: false,
				})),
			)
		},
		[storeApi],
	)
	const getClampedClientPosition = useCallback(
		(point?: { x: number; y: number }) => {
			const containerRect = containerRef.current?.getBoundingClientRect()
			if (!containerRect) {
				return point ?? pointerClientRef.current ?? { x: 0, y: 0 }
			}
			const fallbackPoint = point ??
				pointerClientRef.current ?? {
					x: containerRect.left + containerRect.width / 2,
					y: containerRect.top + containerRect.height / 2,
				}
			const padding = 24
			return {
				x: Math.min(
					containerRect.right - padding,
					Math.max(containerRect.left + padding, fallbackPoint.x),
				),
				y: Math.min(
					containerRect.bottom - padding,
					Math.max(containerRect.top + padding, fallbackPoint.y),
				),
			}
		},
		[],
	)
	const openCreatePalette = useCallback(
		(point?: { x: number; y: number }) => {
			const clientPosition = getClampedClientPosition(point)
			setSelectedNodeIds([])
			setPaletteState({
				mode: "create",
				draftPosition: reactFlow.screenToFlowPosition(clientPosition),
			})
		},
		[getClampedClientPosition, reactFlow, setSelectedNodeIds],
	)
	const closePalette = useCallback(() => {
		setPaletteState((current) =>
			current.mode === "closed" ? current : { mode: "closed" },
		)
	}, [])
	const focusNodeSelection = useCallback(
		(nodeId: string) => {
			let retries = 0
			const applySelection = () => {
				if (
					!storeApi.getState().nodes.some((node) => node.id === nodeId) &&
					retries < 24
				) {
					retries += 1
					setTimeout(applySelection, 16)
					return
				}
				setSelectedNodeIds([nodeId])
				setPaletteState({ mode: "selection" })
			}
			applySelection()
		},
		[setSelectedNodeIds, storeApi],
	)
	const createNodeFromPalette = useCallback(
		(template: BuiltInNodeTemplate) => {
			if (!projectId || paletteState.mode !== "create") return
			const nodeId = uuidv7()
			z.mutate(
				mutators.nodes.add({
					code: template.code,
					id: nodeId,
					inputs: template.inputs,
					label: template.label,
					metadata: {
						...template.metadata,
						default_dimensions: template.defaultDimensions,
					},
					outputs: template.outputs,
					position: paletteState.draftPosition,
					project_id: projectId,
					type: template.type,
				}),
			)
			focusNodeSelection(nodeId)
		},
		[focusNodeSelection, paletteState, projectId, z],
	)
	const createNodesFromImages = useCallback(
		async (files: File[], clientPosition: { x: number; y: number }) => {
			if (!projectId) return
			const imageFiles = files.filter(isImageFile)
			const oversized = imageFiles.filter(
				(file) => file.size > MAX_IMAGE_NODE_FILE_SIZE,
			)
			if (oversized.length > 0) {
				toast.error(
					oversized.length === 1
						? `${oversized[0]?.name ?? "Image"} is larger than 10 MB`
						: `${oversized.length} images are larger than 10 MB`,
				)
			}
			const acceptedFiles = imageFiles.filter(
				(file) => file.size <= MAX_IMAGE_NODE_FILE_SIZE,
			)
			if (acceptedFiles.length === 0) return

			const dropPosition = reactFlow.screenToFlowPosition(clientPosition)
			const createdNodeIds: string[] = []
			for (const [index, file] of acceptedFiles.entries()) {
				try {
					const image = await readImageFileAsDataURL(file)
					const nodeId = uuidv7()
					createdNodeIds.push(nodeId)
					z.mutate(
						mutators.nodes.add({
							code: imageNodeCode,
							id: nodeId,
							inputs: {
								image: {
									description: "Image data",
									required: true,
									type: "image",
									value: image,
								},
								name: {
									description: "Image name",
									required: false,
									type: "string",
									value: file.name,
								},
							},
							label: imageNodeLabel(file.name),
							metadata: {
								built_in_kind: "image",
								content_type: file.type,
								file_name: file.name,
								file_size: file.size,
							},
							outputs: ["image"],
							position: {
								x: dropPosition.x + index * 32,
								y: dropPosition.y + index * 32,
							},
							project_id: projectId,
							type: "react",
						}),
					)
				} catch {
					toast.error(`Could not read ${file.name}`)
				}
			}
			const lastCreatedNodeId = createdNodeIds.at(-1)
			if (lastCreatedNodeId) focusNodeSelection(lastCreatedNodeId)
		},
		[focusNodeSelection, projectId, reactFlow, z],
	)

	const deleteNodesFromPalette = useCallback(
		(nodeIds: string[]) => {
			if (!projectId || nodeIds.length === 0) return

			for (const nodeId of nodeIds) {
				z.mutate(
					mutators.nodes.delete({
						project_id: projectId,
						node_id: nodeId,
					}),
				)
			}

			setPaletteState({ mode: "closed" })
		},
		[projectId, z],
	)
	useEffect(() => {
		const handlePointerMove = (event: PointerEvent) => {
			pointerClientRef.current = { x: event.clientX, y: event.clientY }
		}
		window.addEventListener("pointermove", handlePointerMove, { passive: true })
		return () => window.removeEventListener("pointermove", handlePointerMove)
	}, [])
	useEffect(() => {
		setPaletteState((current) => {
			if (current.mode === "create") {
				return selectedNodes.length > 0 ? { mode: "selection" } : current
			}
			if (selectedNodes.length === 0) {
				return current.mode === "selection" ? { mode: "closed" } : current
			}
			return current.mode === "selection" ? current : { mode: "selection" }
		})
	}, [selectedNodes.length])

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const isCommandKey = event.metaKey || event.ctrlKey
			const key = event.key.toLowerCase()

			if (isCommandKey && key === "k" && !isEditableTarget(event.target)) {
				event.preventDefault()
				openCreatePalette()
				return
			}

			if (!isCommandKey || event.shiftKey || event.altKey) return
			if (isEditableTarget(event.target)) return

			if (key === "c") {
				const textSelection = window.getSelection()
				if (textSelection && !textSelection.isCollapsed) return
				if (copySelection()) event.preventDefault()
				return
			}

			if (key === "v") {
				if (!hasClipboardSelection()) return
				event.preventDefault()
				void pasteSelection()
			}
		}

		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [copySelection, hasClipboardSelection, openCreatePalette, pasteSelection])

	return (
		<div ref={containerRef} className="relative h-full w-full bg-muted">
			<ReactFlow
				className="relative"
				nodes={nodes}
				edges={edges}
				onNodesChange={(changes) => {
					setTimeout(() => onNodesChange(changes), 0)
				}}
				onEdgesChange={(changes) => {
					setTimeout(() => onEdgesChange(changes), 0)
				}}
				onConnect={handlers.onConnect}
				onConnectStart={handlers.onConnectStart}
				onConnectEnd={handlers.onConnectEnd}
				onNodesDelete={handlers.onNodesDelete}
				selectionMode={selection.selectionMode}
				onSelectionStart={selection.onSelectionStart}
				onSelectionEnd={selection.onSelectionEnd}
				onPointerMove={selection.onSelectionPointerMove}
				nodeTypes={nodeTypes}
				onNodeDrag={handlers.onNodeDrag}
				onNodeDragStart={handlers.onNodeDragStart}
				onNodeDragStop={handlers.onNodeDragStop}
				onSelectionDrag={handlers.onSelectionDrag}
				onSelectionDragStart={handlers.onSelectionDragStart}
				onSelectionDragStop={handlers.onSelectionDragStop}
				edgeTypes={edgeTypes}
				style={{
					...selection.selectionBoxStyles,
					backgroundColor: "transparent",
				}}
				panOnDrag={[1, 2]}
				panOnScroll={true}
				zoomOnPinch={true}
				zoomOnDoubleClick={false}
				zoomOnScroll={true}
				selectionOnDrag={true}
				deleteKeyCode="Delete"
				onPaneClick={closePalette}
				onDragOver={(event) => {
					if (
						!Array.from(event.dataTransfer.items).some(
							(item) =>
								item.kind === "file" &&
								(!item.type || item.type.startsWith("image/")),
						)
					) {
						return
					}
					event.preventDefault()
					event.dataTransfer.dropEffect = "copy"
				}}
				onDrop={(event) => {
					const files = Array.from(event.dataTransfer.files).filter(isImageFile)
					if (files.length === 0) return
					event.preventDefault()
					void createNodesFromImages(files, {
						x: event.clientX,
						y: event.clientY,
					})
				}}
				onDoubleClick={(event) => {
					if (
						!(event.target instanceof Element) ||
						!event.target.classList.contains("react-flow__pane")
					) {
						return
					}
					event.preventDefault()
					openCreatePalette({ x: event.clientX, y: event.clientY })
				}}
				connectionLineStyle={{
					strokeWidth: 5,
					stroke: "var(--node-selection)",
					opacity: 0.92,
				}}
				connectionLineComponent={NodeConnectionLine}
				connectionLineContainerStyle={{ zIndex: 20 }}
				connectionLineType={ConnectionLineType.SmoothStep}
				defaultEdgeOptions={{
					type: "oblique",
					zIndex: 20,
				}}
				zIndexMode="manual"
				proOptions={{ hideAttribution: true }}
			>
				<Background
					gap={24}
					size={2}
					style={{ opacity: 0.15 }}
					className="bg-muted/50!"
				/>
				<CustomControls />
				<CursorLayer />
				<DragPreview />
				<ConnectionPreview />
				<SelectionPreview />

				<CommandPalette
					state={paletteState}
					selectedNodes={selectedNodes}
					onClose={closePalette}
					onCreateNode={createNodeFromPalette}
					onDeleteNodes={deleteNodesFromPalette}
				/>
			</ReactFlow>
		</div>
	)
}
